import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'

import social from '@/assets/social.png?url'
import BlogAuthors from '@/components/BlogAuthors'
import BlogCategoryLinks from '@/components/BlogCategoryLinks'
import BlogCategoryNav from '@/components/BlogCategoryNav'
import BlogPostLink from '@/components/BlogPostLink'
import Paginator from '@/components/Paginator'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import { findBlogCategory, findPaginatedBlogsByCategory } from '@/models/blog.server'
import { pageHelper } from '@/request'
import { formatLong } from '@/utils/date'

type LoaderData = typeof loader

export async function loader({ params, url }: LoaderFunctionArgs) {
  const category = findBlogCategory(params.slug as string)

  if (!category) {
    throw new Response('Not Found', { status: 404 })
  }

  const page = pageHelper(url)

  const { blogs, pagination } = await findPaginatedBlogsByCategory(category, { page: page, perPage: 25 })
  const { root } = page

  return {
    blogs: blogs,
    category: category,
    pagination: pagination,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const { category, root } = loaderData

  const title = 'Blog — PlanetScale'
  const description = 'Posts about the PlanetScale platform, MySQL, PostgreSQL, databases, and more.'
  const image = `${root}${social}`

  return [
    { title },
    { name: 'description', content: description },
    { name: 'robots', content: 'noindex' },
    { property: 'og:url', content: `https://planetscale.com/blog/category/${category}` },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: `https://planetscale.com/blog/category/${category}` },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: 'https://planetscale.com/blog' }
  ]
}

export default function BlogCategory() {
  const { blogs, pagination } = useLoaderData<LoaderData>()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <section>
          <h1>Blog</h1>

          <BlogCategoryNav />

          {blogs.map((blog) => (
            <div key={blog.slug}>
              <hr className='dot-fill' />

              <div className='flex flex-col gap-0 md:flex-row md:space-x-1'>
                <h2>
                  <BlogPostLink
                    blog={blog}
                    className='hover:bg-gray-900 hover:text-white hover:no-underline dark:hover:bg-gray-50 dark:hover:text-black'
                  >
                    {blog.title}
                  </BlogPostLink>
                </h2>
                <span className='pb-1 text-secondary'>
                  [<BlogCategoryLinks categories={blog.categories} />]
                </span>
              </div>

              <p className='flex flex-col text-secondary sm:flex-row'>
                <span>
                  <BlogAuthors authors={blog.authors} /> <span className='hidden pr-1 sm:inline'>|</span>
                </span>
                <time dateTime={blog.createdAt}>{formatLong(blog.createdAt)}</time>
              </p>

              <p>
                {blog.excerpt}{' '}
                <BlogPostLink
                  blog={blog}
                  className='text-default bg-blue-100 px-sm hover:bg-blue-600 hover:text-white dark:bg-blue-900 dark:text-blue dark:hover:bg-blue-500 dark:hover:text-black'
                >
                  →
                </BlogPostLink>
              </p>
            </div>
          ))}

          <Paginator pagination={pagination} />
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
